import { useEffect, useCallback, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import _ from "lodash"

import config from "../../config"

import {
  fetchAppManifest,
  fetchMapLabels,
  fetchMapDecorations,
  fetchPagesContent,
} from "../../services/venue"
import { VenueContext } from "./VenueContext"
import { useSearch } from "./hooks/useSearch.ts"
import { useTheme } from "./hooks/useTheme.ts"

import { prepareGeolocationFeature } from "providers/geolocation/utils/prepareGeolocationFeature"
import {
  prepareMapDecorations,
  preparePagesContent,
} from "./utils/prepareFeature"

import {
  createGeolocationFeature,
  isLatLngQueryString,
} from "utils/geolocation"

import { setupVenueControllerConfig } from "./utils/prepareConfig"

import {
  useGetImdf,
  useGetAppConfig,
  useGetSponsoredContents,
  useGetProducts,
  useGetModel3ds,
} from "./modules/data"
import { useShortestPathStrategy } from "./modules/shortestPath/useShortestPathStrategy/useShortestPathStrategy"
import { useGetList } from "venue-react"

const LOCALE_CONFIG = {
  en: {
    localeKey: "en",
    name: "English",
    flagCode: "gb",
    localizedName: "English",
  },
  th: {
    localeKey: "th",
    name: "Thai",
    flagCode: "th",
    localizedName: "ไทย",
  },
  zh: {
    localeKey: "zh",
    name: "Chinese",
    flagCode: "cn",
    localizedName: "中文",
  },
  ja: {
    localeKey: "ja",
    name: "Japanese",
    flagCode: "jp",
    localizedName: "日本語",
  },
  ar: {
    localeKey: "ar",
    name: "Arabic",
    flagCode: "ar",
    localizedName: "العربية",
  },
}

const DEFAULT_LOCALE_SUPPORT_LIST = ["en", "th"]

export const defaultAppConfig = {
  map: {},
}

export const VenueProvider = ({ children }) => {
  // Modules
  /** State, Hooks */
  const deviceType = config("device", "mobile")

  /**
   * Example Extension Configs
    {
      "extensions": {
          "app": [
            {
              "to": "promotions", // <-- path for registering route
              "component": "tourist-friendly/pages/promotions" // <-- component path for loading
            },
            ....
          ]
        }
      }
    }
  */
  const { data: appManifest } = useQuery({
    queryKey: ["appManifest"],
    queryFn: fetchAppManifest,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  const { data: imdfData, isFetched, refetch: refetchImdfData } = useGetImdf()
  const { data: events = [] } = useGetList(
    "event",
    { populate: true },
    { refetchOnWindowFocus: false }
  )
  const { data: promotions = [] } = useGetList(
    "promotion",
    { populate: true },
    { refetchOnWindowFocus: false }
  )
  const { data: products } = useGetProducts()
  const { data: model3ds } = useGetModel3ds()

  /** Load Application Config */
  const { data: appConfig, isFetched: isConfigFetched } = useGetAppConfig()

  const {
    mapConfig,
    stepAutoplayConfig,
    parkingFinderConfig,
    pageConfig,
    idleModeConfig,
    iframeBridgeConfig,
    searchConfig,
    featureQuickSearchConfig,
    venueFeatureQuickSearchConfig,
    keyboardConfig,
    extensionConfig,
    screenSaverConfig,
    sponsoredContentConfig,
    venueControllerConfig,

    indexPath,
    defaultRoutePath,
  } = appConfig

  if (venueControllerConfig) {
    setupVenueControllerConfig(venueControllerConfig)
  }
  /** End of Load Application Config */

  const { data: sponsoredContent, isFetched: isSponsoredContentFetched } =
    useGetSponsoredContents({
      imdfFeatures: imdfData?.features,
      promotions,
      events,
    })

  const { data: labels } = useQuery({
    queryKey: ["labels"],
    queryFn: fetchMapLabels,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const { data: decorations, isFetched: isDecorationFetched } = useQuery({
    queryKey: ["elements"],
    queryFn: async () => {
      const decorationsData = await fetchMapDecorations()
      return prepareMapDecorations(decorationsData, {
        units: imdfData?.units,
        kiosks: imdfData?.kiosks,
      })
    },
    retry: false,
    refetchOnWindowFocus: false,
    enabled: isFetched,
  })

  //correct locale properties is supported_locales
  const supportedLocales = useMemo(
    () =>
      _.get(
        _.first(imdfData.venues),
        "properties.supported_locales",
        DEFAULT_LOCALE_SUPPORT_LIST
      ),
    [imdfData.venues]
  )

  const locales = useMemo(() => {
    try {
      return _(supportedLocales)
        .map((locale) => _.get(LOCALE_CONFIG, locale))
        .compact()
        .value()
    } catch (error) {
      return _(DEFAULT_LOCALE_SUPPORT_LIST)
        .map((locale) => _.get(LOCALE_CONFIG, locale))
        .compact()
        .value()
    }
  }, [supportedLocales])

  const themeName = config("themeName")
  const mapThemeName = config("mapThemeName")
  const { themeOptions, initVenueTheme, initMapTheme, mapTheme } = useTheme(
    themeName,
    mapThemeName
  )

  const featuresToSearch = useMemo(() => {
    return _(imdfData)
      .pick(["occupants", "amenities", "sections"])
      .values()
      .flatten()
      .value()
  }, [imdfData])

  const { search, isSearchReady, getSuggestedSearchItems } = useSearch(
    featuresToSearch,
    {
      config: searchConfig,
      enable: isConfigFetched,
    }
  )

  const graphOptions = useMemo(
    () => _.get(mapConfig, "prepare_steps", {}),
    [mapConfig]
  )

  const {
    findRoute,
    findElevatorPreferredRoute,
    relationshipGraphLoaded,
    getRelatedLevelWithSteps,
    transformStepsToGeometries,
    normalizeStepInRoute,
  } = useShortestPathStrategy(imdfData, {
    /**
     * ==== Example App Config ====
     * "map": {
     *   ... other configs ...
     *   "shortest_path": {
     *     "resolution": 0.0016,
     *     "include_instructions": true // venue-sdk only; flattens turns into steps
     *   },
     *   "prepare_steps": {
     *     "traversal": {
     *       "room": 100,
     *       "unenclosedarea": 100
     *     },
     *     "escalator": {
     *       "baseDistance": 100,
     *       "multiplier": 1
     *     }
     *     // ...others...
     *   }
     * }
     */
    ..._.get(mapConfig, "shortest_path", {}),
    enabled: isFetched,
    strategy: config("shortestPathStrategy"),
    graphOptions,
  })

  const rootCategories = useMemo(
    () =>
      _.uniqBy(
        _.filter(
          imdfData.categories,
          (feature) => feature.properties.parent_id === null
        ),
        "id"
      ),
    [imdfData.categories]
  )

  const findFeatureById = useCallback(
    (id) => imdfData.features.find((f) => f.id === id),
    [imdfData]
  )

  const findSectionByReferenceId = useCallback(
    (refId) =>
      imdfData.sections.find((s) => s.properties?.reference_id === refId),
    [imdfData.sections]
  )

  const filterOccupantsByName = useCallback(
    (name) => {
      return imdfData.occupants.filter(
        (occupant) => occupant.properties.name.en === name
      )
    },
    [imdfData]
  )

  const transformLocationToFeature = useCallback(
    (feature) => {
      return isLatLngQueryString(feature)
        ? prepareGeolocationFeature(
            createGeolocationFeature(feature, { name: "Your Location" }),
            {
              venues: imdfData.venues,
              levels: imdfData.levels,
              units: imdfData.units,
              sections: imdfData.sections,
            }
          )
        : findFeatureById(feature) || null
    },
    [
      findFeatureById,
      imdfData.venues,
      imdfData.levels,
      imdfData.units,
      imdfData.sections,
    ]
  )

  const filterAmenitiesByCategory = useCallback(
    (category) => {
      return imdfData.amenities.filter(
        (amenityFeature) => amenityFeature.properties.category === category
      )
    },
    [imdfData.amenities]
  )

  const filterOccupantsByRootCategory = useCallback(
    (category) => {
      return imdfData.occupants.filter(
        ({ feature_type, properties }) =>
          feature_type === "occupant" &&
          properties.anchor &&
          (category === undefined ||
            properties.local_parent_categories?.[0]?.id === category)
      )
    },
    [imdfData.occupants]
  )

  const isMultiOrdinalVenue = useMemo(() => {
    return _.uniqBy(imdfData.levels, "properties.ordinal").length > 1
  }, [imdfData.levels])

  const isMultiVenueProject = useMemo(() => {
    return imdfData.venues.length > 1
  }, [imdfData.venues])

  // TODO: Find and Implement default Venue
  const defaultVenue = useMemo(() => _.first(imdfData.venues), [imdfData])

  const { data: pagesContent, isFetched: isPagesContentFetched } = useQuery({
    queryKey: ["pagesContent"],
    queryFn: async () => {
      const pagesData = await fetchPagesContent()
      return preparePagesContent(pagesData)
    },
    retry: false,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (!appManifest) return
    initVenueTheme()
    initMapTheme()
  }, [appManifest, initVenueTheme, initMapTheme])

  useEffect(() => {
    const favicon = appManifest?.icons?.find(
      (icon) => icon.type === "image/x-icon"
    )
    if (favicon) {
      document.getElementById("favicon").href = favicon.src
    }
  }, [appManifest])
  const value = {
    ...imdfData,
    // Realtime
    events,
    promotions,
    products,
    labels,
    model3ds,
    decorations,
    themeOptions,
    appManifest,
    mapTheme,
    locales,
    supportedLocales,
    localeConfig: LOCALE_CONFIG,
    rootCategories,
    findRoute,
    isMultiOrdinalVenue,
    isMultiVenueProject,
    findElevatorPreferredRoute,
    /** App Config */
    appConfig,
    stepAutoplayConfig,
    parkingFinderConfig,
    idleModeConfig,
    iframeBridgeConfig,
    extensionConfig,

    /** TODO: Remove these hooks once all consumers use venue-react's useGetList
     * - ./hooks/sponsored-content/useSponsoredHomepageSpotlight
     * - ./hooks/sponsored-content/useSponsoredPopupBanner
     * - ./hooks/sponsored-content/useSponsoredSearchResult
     * - ./hooks/sponsored-content/useSponsoredSuggestedBrand
     * - ./hooks/sponsored-content/useSponsoredContent
     *
     *  @see useGetList from venue-react
     * */
    sponsoredContent,

    defaultVenue,
    featureQuickSearchConfig,
    venueFeatureQuickSearchConfig,
    venueControllerConfig,
    keyboardConfig,
    pageConfig,
    pagesContent,
    screenSaverConfig,
    sponsoredContentConfig,

    // Flag
    relationshipGraphLoaded,
    configLoaded: isConfigFetched,
    themeLoaded: !!themeOptions,
    dataLoaded:
      isFetched &&
      isDecorationFetched &&
      isConfigFetched &&
      isSponsoredContentFetched &&
      isPagesContentFetched,
    mapConfig,
    indexPath,
    defaultRoutePath,
    deviceType,
    isSearchReady,
    // functions
    refetchImdfData,
    findFeatureById,
    findSectionByReferenceId,
    search,
    filterAmenitiesByCategory,
    filterOccupantsByName,
    filterOccupantsByRootCategory,
    transformLocationToFeature,
    getRelatedLevelWithSteps,
    transformStepsToGeometries,
    normalizeStepInRoute,
    getSuggestedSearchItems,
  }

  return <VenueContext.Provider value={value}>{children}</VenueContext.Provider>
}
